<template>
  <v-card>
    <v-card-text class="px-4 py-6">
      <div
        class="subtitle1 mb-2"
        v-text="$t('network.secureWebGateway.allowlistFiltering.title')"
      />
      <div
        class="body1 text-indigo-medium mb-4"
        v-text="$t('network.secureWebGateway.allowlistFiltering.description')"
      />
      <v-autocomplete
        v-model="localLabels"
        :items="availableLabels"
        :label="localLabels.length ? $t('network.secureWebGateway.labelsAutocompleteLabel') : ''"
        :placeholder="
          !localLabels.length ? $t('network.secureWebGateway.labelsAutocompletePlaceholder') : ''
        "
        :disabled="isLoading"
        variant="outlined"
        multiple
        return-object
        chips
        closable-chips
        clear-on-select
        item-value="id"
        item-title="name"
        hide-details
      >
        <template #chip="{ props, item }">
          <v-chip
            v-bind="props"
            :closable="true"
            size="default"
            :color="item.raw.color"
            close-icon="$close"
            :text="item.raw.name"
            :disabled="isLoading"
            variant="flat"
            label
            rounded="3"
          >
            <span class="ml-1 mr-2 coro-label text-uppercase text-primary" v-text="item.raw.name" />
          </v-chip>
        </template>
        <template #item="{ props, item }">
          <v-list-item v-bind="props" title="">
            <template #default>
              <div class="d-flex">
                <div class="color-preview mr-2" :style="{ 'background-color': item.raw.color }" />
                <span v-text="item.raw.name" />
              </div>
            </template>
          </v-list-item>
        </template>
      </v-autocomplete>

      <div class="d-flex align-start mt-2 pl-4">
        <v-icon icon="$infoXs" size="16" class="mr-1 flex-shrink-0 info-icon" />
        <span
          class="body3 text-indigo-medium"
          v-text="$t('network.secureWebGateway.allowlistFiltering.labelsAssistiveText')"
        />
      </div>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import { defineComponent, computed, type PropType } from "vue";
import type { DeviceLabel } from "@/_store/devices-settings.module.ts";

export default defineComponent({
  props: {
    labels: {
      type: Array as PropType<DeviceLabel[]>,
      default: () => [],
    },
    availableLabels: {
      type: Array as PropType<DeviceLabel[]>,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:labels"],
  setup(props, { emit }) {
    const toLabelsItems = (serverLabels: DeviceLabel[]): DeviceLabel[] =>
      serverLabels
        .map(({ id }) => props.availableLabels.find((l) => l.id === id))
        .filter((l): l is DeviceLabel => !!l);

    const localLabels = computed({
      get: () => toLabelsItems(props.labels),
      set: (newVal: DeviceLabel[]) => {
        emit("update:labels", newVal);
      },
    });

    const isLoading = computed(() => props.loading);

    return {
      localLabels,
      isLoading,
    };
  },
});
</script>

<style scoped lang="scss">
.info-icon {
  &:before {
    color: rgb(var(--v-theme-indigo-medium));
  }
}
.color-preview {
  width: 20px !important;
  height: 20px !important;
  min-width: 20px !important;
  border-radius: 2px !important;
}
</style>
